/**
 * 逻辑流编辑器 - dagre 自动布局
 *
 * 使用 Sugiyama 分层布局算法：
 *   1. 分层（rank） — 根据边方向将节点分配到层
 *   2. 排序（order） — 层内节点重排以最小化边交叉
 *   3. 坐标计算 — 确定每个节点的精确 x/y
 */

import dagre from 'dagre';
import type {LogicFlowEdge, LogicFlowNode} from '../types/logicFlow';

export interface LayoutPosition {
    nodeId: string;
    x: number;
    y: number;
}

const NODE_WIDTH = 200;
const NODE_HEIGHT = 90;

/** dagre 布局配置 */
const DAGRE_CONFIG = {
    rankdir: 'LR',
    nodesep: 80,
    ranksep: 150,
    marginx: 40,
    marginy: 40,
};

/**
 * 对逻辑流图执行自动布局
 * @returns Map<nodeId, position> — dagre 计算出的节点左上角坐标
 */
export function autoLayout(graph: { nodes: LogicFlowNode[]; edges: LogicFlowEdge[] }): Map<string, LayoutPosition> {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph(DAGRE_CONFIG);

    for (const node of graph.nodes) {
        g.setNode(node.id, {width: NODE_WIDTH, height: NODE_HEIGHT});
    }

    for (const edge of graph.edges) {
        g.setEdge(edge.source, edge.target);
    }

    dagre.layout(g);

    const positions = new Map<string, LayoutPosition>();
    for (const node of graph.nodes) {
        const dagreNode = g.node(node.id);
        if (dagreNode) {
            positions.set(node.id, {
                nodeId: node.id,
                x: dagreNode.x - NODE_WIDTH / 2,
                y: dagreNode.y - NODE_HEIGHT / 2,
            });
        }
    }

    return positions;
}
